import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import { SubHeader } from "@/components/SubHeader";
import { Reveal } from "@/components/Reveal";
import { API } from "@/lib/site";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    axios
      .get(`${API}/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        setStatus("ok");
        document.title = `${data.title} | Healthcare Solutions`;
      })
      .catch(() => setStatus("missing"));
    return () => {
      document.title = "Healthcare Solutions | Connecting Healthcare. Creating Solutions.";
    };
  }, [slug]);

  return (
    <div data-testid="article-page" className="min-h-screen bg-white">
      <SubHeader crumb="Healthcare Knowledge" />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-32 sm:px-6">
        {status === "loading" && (
          <p className="text-center text-sm text-[#6B8AA0]">Loading article...</p>
        )}
        {status === "missing" && (
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-[#0B3C5C]">Article not found</h1>
            <Link to="/knowledge" data-testid="article-back-link" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#4C8F2F] hover:text-[#5FA832]">
              <ArrowLeft className="h-4 w-4" /> Back to Knowledge Hub
            </Link>
          </div>
        )}
        {status === "ok" && article && (
          <Reveal>
            <Link to="/knowledge" data-testid="article-back-link" className="inline-flex items-center gap-2 text-xs font-semibold text-[#4A6B82] transition-colors hover:text-[#5FA832]">
              <ArrowLeft className="h-3.5 w-3.5" /> All Articles
            </Link>
            <div className="mt-8 flex items-center gap-3">
              <span className="font-mono-x rounded-full border border-teal-500/30 bg-[#5FA832]/10 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-[#5FA832]">
                {article.category}
              </span>
              <span className="text-xs text-[#6B8AA0]">
                {new Date(article.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
              </span>
            </div>
            <h1 className="font-display mt-6 text-3xl font-extrabold leading-tight tracking-tight text-[#0B3C5C] sm:text-4xl">
              {article.title}
            </h1>
            <p className="mt-5 border-l-2 border-teal-500 pl-5 text-base italic leading-relaxed text-[#33586F]">
              {article.excerpt}
            </p>
            {article.cover_image && (
              <div className="mt-8 overflow-hidden rounded-[2rem] border border-[#0B3C5C]/15 shadow-2xl shadow-black/40">
                <img
                  src={article.cover_image}
                  alt={article.title}
                  className="aspect-[21/9] w-full object-cover"
                />
              </div>
            )}
            <div className="mt-10 space-y-6" data-testid="article-content">
              {article.content.split(/\n{2,}/).map((para, i) => (
                <p key={i} className="text-base leading-relaxed text-[#33586F]">
                  {para}
                </p>
              ))}
            </div>

            <div className="mt-14 flex items-start gap-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <p className="text-xs leading-relaxed text-[#4A6B82] sm:text-sm">
                This article is intended for general awareness and educational purposes. It is not a
                substitute for medical diagnosis, treatment or professional medical advice. Insurance
                and claim-related information is subject to the applicable policy terms, insurer/TPA
                processes and final decisions of the relevant parties.
              </p>
            </div>

            <div className="mt-12 rounded-[2rem] border border-[#0B3C5C]/10 bg-gradient-to-br from-white to-[#EAF3F8] p-9 text-center">
              <h2 className="font-display text-xl font-bold text-[#0B3C5C]">Have a healthcare requirement?</h2>
              <p className="mt-2 text-sm text-[#4A6B82]">Tell us what you are looking for and our team will get in touch.</p>
              <Link
                to="/#enquiry"
                data-testid="article-cta-enquiry"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F]"
              >
                Request a Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        )}
      </main>
    </div>
  );
}
